import { Component, Input, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

interface TyreSlot {
  id: string;
  label: string;
  isActive: boolean;
  color?: string;
}

interface TyreConfiguration {
  name: string;
  type: string;
  rows: number;
  cols: number;
  slots: TyreSlot[];
  slotCount: number;
  numDrives?: number;
  slotsPerDrive?: number;
  hasSpare?: boolean;
  numAxles?: number; // for trailer
}

interface TyreAssignment {
  id: string;
  tyreNumber: string;
  component: 'Horse' | 'Trailer';
  axlePosition: string;
  sideSlot: string;
  selectedSlot?: string;
}

@Component({
  selector: 'app-assign-tyre-position-component',
  templateUrl: './assign-tyre-position-component.component.html',
  styleUrls: ['./assign-tyre-position-component.component.css']
})
export class AssignTyrePositionComponentComponent implements OnInit {
  @Input() data: any;

  // Configuration received from parent
  configType: string = '6x4'; // Default, will be overridden by data
  horseConfig!: TyreConfiguration;
  trailerConfig!: TyreConfiguration;

  // All configurations
  configurations: { [key: string]: { horse: TyreConfiguration; trailer: TyreConfiguration } } = {
    '4x2': {
      horse: {
        name: 'HORSE - 4 TYRE SLOTS',
        type: 'Horse',
        rows: 4,
        cols: 2,
        slots: [],
        slotCount: 4,
        numDrives: 1,
        slotsPerDrive: 2,
        hasSpare: false
      },
      trailer: {
        name: 'TRAILER - 4 TYRE SLOTS',
        type: 'Trailer',
        rows: 1,
        cols: 4,
        slots: [],
        slotCount: 4,
        numAxles: 1
      }
    },
    '4x4': {
      horse: {
        name: 'HORSE - 4 TYRE SLOTS',
        type: 'Horse',
        rows: 4,
        cols: 2,
        slots: [],
        slotCount: 4,
        numDrives: 1,
        slotsPerDrive: 2,
        hasSpare: false
      },
      trailer: {
        name: 'TRAILER - 4 TYRE SLOTS',
        type: 'Trailer',
        rows: 1,
        cols: 4,
        slots: [],
        slotCount: 4,
        numAxles: 1
      }
    },
    '6x2': {
      horse: {
        name: 'HORSE - 6 TYRE SLOTS',
        type: 'Horse',
        rows: 6,
        cols: 2,
        slots: [],
        slotCount: 6,
        numDrives: 2,
        slotsPerDrive: 2,
        hasSpare: false
      },
      trailer: {
        name: 'TRAILER - 6 TYRE SLOTS',
        type: 'Trailer',
        rows: 2,
        cols: 4,
        slots: [],
        slotCount: 6,
        numAxles: 2
      }
    },
    '8x4': {
      horse: {
        name: 'HORSE - 8 TYRE SLOTS',
        type: 'Horse',
        rows: 4,
        cols: 2,
        slots: [],
        slotCount: 8,
        numDrives: 3,
        slotsPerDrive: 2,
        hasSpare: false
      },
      trailer: {
        name: 'TRAILER - 8 TYRE SLOTS',
        type: 'Trailer',
        rows: 2,
        cols: 4,
        slots: [],
        slotCount: 8,
        numAxles: 2
      }
    },
   '10x4': {
      horse: {
        name: 'HORSE - 12 TYRE SLOTS',
        type: 'Horse',
        rows: 10,
        cols: 2,
        slots: [],
        slotCount: 20,
        numDrives: 4,
        slotsPerDrive: 4,
        hasSpare: true
      },
      trailer: {
        name: 'TRAILER - 8 TYRE SLOTS',
        type: 'Trailer',
        rows: 2,
        cols: 4,
        slots: [],
        slotCount: 8,
        numAxles: 2
      }
    },
    '6x4': {
      horse: {
        name: 'HORSE - 12 TYRE SLOTS',
        type: 'Horse',
        rows: 6,
        cols: 2,
        slots: [],
        slotCount: 12,
        numDrives: 2,
        slotsPerDrive: 4,
        hasSpare: true
      },
      trailer: {
        name: 'TRAILER - 8 TYRE SLOTS',
        type: 'Trailer',
        rows: 2,
        cols: 4,
        slots: [],
        slotCount: 8,
        numAxles: 2
      }
    }
  };

  // Tyre assignments
  tyreAssignments: TyreAssignment[] = [];
  currentHighlightedTyre: string | null = null;

  // Form options
  components: ('Horse' | 'Trailer')[] = ['Horse', 'Trailer'];

  // Statistics
  totalTyresSelected = 0;
  assignedToHorse = 0;
  assignedToTrailer = 0;
  selectedTypePosition:any;
  selectedItem:any;
  noOfAxel:any
  isEdit:any;
  savedComponents:any;
  constructor(public ref: DynamicDialogRef,public config: DynamicDialogConfig) {}
  ngOnInit(): void {
    // Get configuration from input data
    this.data = this.config.data
    if (this.data && this.data.configType) {
      this.configType = this.data.configType;
    }
    this.selectedTypePosition = this.config.data?.type
    this.selectedItem = this.config.data?.selectedTyres
    this.noOfAxel = this.config.data?.axle
    this.isEdit = this.config?.data?.isEdit
    this.savedComponents = this.config?.data?.savedComponents
    this.configurations[this.data.configType].trailer.rows = this.noOfAxel
    this.configurations[this.data.configType].trailer.numAxles = this.noOfAxel
    if (this.selectedTypePosition === 'truck') {
      this.components = ['Horse'];
    } else if (this.selectedTypePosition === 'trailer') {
      this.components = ['Trailer'];
    }
    this.initializeConfigurations();
    this.initializeTyreAssignments();
    this.updateSlotActives();
  }

  // Helper method for string padding in template
  padStart(num: number): string {
    return String(num).padStart(2, '0');
  }

  getAxlePositions(component: 'Horse' | 'Trailer'): string[] {
    if (component === 'Horse') {
      const positions: string[] = ['Steer Axle'];
      const numDrives = this.horseConfig.numDrives || 0;
      for (let i = 1; i <= numDrives; i++) {
        positions.push(`Drive Axle ${i}`);
      }
      if (this.horseConfig.hasSpare) {
        positions.push('Spare');
      }
      return positions;
    } else {
      const numAxles = this.trailerConfig.numAxles || 2;
      const positions: string[] = [];
      for (let i = 1; i <= numAxles; i++) {
        positions.push(`Axle ${i}`);
      }
      return positions;
    }
  }

  getSideSlots(component: 'Horse' | 'Trailer', axlePosition?: string): string[] {
    let allSlots: string[] = [];
    let totalSlots = 0;
    if (component === 'Horse') {
      allSlots = ['Front Left (F-L1)', 'Front Right (F-R1)'];
      const numDrives = this.horseConfig.numDrives || 0;
      const slotsPerDrive = this.horseConfig.slotsPerDrive || 2;
      const isDual = slotsPerDrive === 4;
      for (let d = 1; d <= numDrives; d++) {
        if (isDual) {
          const outer = 2 * d - 1;
          const inner = 2 * d;
          allSlots.push(
            `Rear Left Outer (R-L${outer})`,
            `Rear Left Inner (R-L${inner})`,
            `Rear Right Outer (R-R${outer})`,
            `Rear Right Inner (R-R${inner})`
          );
        } else {
          allSlots.push(
            `Rear Left (R-L${d})`,
            `Rear Right (R-R${d})`
          );
        }
      }
      if (this.horseConfig.hasSpare) {
        allSlots.push('Spare 1 (SP-1)', 'Spare 2 (SP-2)');
      }
      totalSlots = this.horseConfig.slotCount;
    } else {
      // Trailer
      const numAxles = this.trailerConfig.numAxles || 2;
      let labelIndex = 1;
      for (let a = 1; a <= numAxles; a++) {
        const left1 = labelIndex;
        const left2 = labelIndex + 1;
        const right1 = labelIndex;
        const right2 = labelIndex + 1;
        if (allSlots.length >= this.trailerConfig.slotCount) break;
        if (allSlots.length < this.trailerConfig.slotCount) allSlots.push(`Trailer Left ${left1} (T-L${left1})`);
        if (allSlots.length < this.trailerConfig.slotCount) allSlots.push(`Trailer Left ${left2} (T-L${left2})`);
        if (allSlots.length < this.trailerConfig.slotCount) allSlots.push(`Trailer Right ${right1} (T-R${right1})`);
        if (allSlots.length < this.trailerConfig.slotCount) allSlots.push(`Trailer Right ${right2} (T-R${right2})`);
        labelIndex += 2;
      }
      totalSlots = this.trailerConfig.slotCount;
    }

    let slots = allSlots;
    if (axlePosition) {
      let start = 0;
      let axleSize = 0;
      const slotsPerDrive = this.horseConfig.slotsPerDrive || 2;
      if (component === 'Horse') {
        if (axlePosition === 'Steer Axle') {
          start = 0;
          axleSize = 2;
        } else if (axlePosition.startsWith('Drive Axle ')) {
          const n = parseInt(axlePosition.match(/\d+/)![0]);
          start = 2 + (n - 1) * slotsPerDrive;
          axleSize = slotsPerDrive;
        } else if (axlePosition === 'Spare') {
          start = totalSlots - 2;
          axleSize = 2;
        }
      } else {
        const n = parseInt(axlePosition.split(' ')[1]);
        start = (n - 1) * 4;
        axleSize = 4;
      }
      const end = Math.min(start + axleSize, totalSlots);
      slots = allSlots.slice(start, end);
    }
    return slots;
  }

  setTyreComponent(tyre: TyreAssignment, component: 'Horse' | 'Trailer'): void {
    tyre.component = component;
    const firstAxle = this.getAxlePositions(component)[0];
    // Use onAxleChange to reset sideSlot properly
    this.onAxleChange(tyre, firstAxle);
    this.updateStatistics();
  }

  onAxleChange(tyre: TyreAssignment, newAxle: string): void {
    tyre.axlePosition = newAxle;
    // Reset sideSlot to first option for this axle (prevents invalid selection)
    const validSlots = this.getSideSlots(tyre.component, newAxle);
    if (validSlots.length > 0) {
      tyre.sideSlot = validSlots[0];
    }
    // Trigger full update
    this.onTyreChange(tyre);
  }

  onTyreChange(tyre: TyreAssignment): void {
    console.log('onTyreChange called:', { tyreId: tyre.id, axle: tyre.axlePosition, sideSlot: tyre.sideSlot });

    // Improved extraction: Pull from parens, fallback to full if no parens
    let extractedSlot: string | undefined;
    const match = tyre.sideSlot.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      extractedSlot = match[1]; // e.g., "R-R4"
    } else {
      // Fallback: Try to extract from common patterns (e.g., if no parens)
      extractedSlot = tyre.sideSlot.match(/([A-Z]-\w+\d+)/)?.[1];
    }

    tyre.selectedSlot = extractedSlot || undefined;
    console.log('Extracted slot:', extractedSlot);

    // Update visual map
    this.updateSlotActives();
    // Highlight the current tyre
    this.currentHighlightedTyre = tyre.id;
  }

  getAxleNumbers(component: string): number[] {
    if (component === 'horse') {
      const numDrives = this.horseConfig.numDrives || 0;
      return Array.from({ length: numDrives }, (_, i) => i + 1);
    } else {
      const numAxles = this.trailerConfig.numAxles || 2;
      return Array.from({ length: numAxles }, (_, i) => i + 1);
    }
  }

  getAxleStart(index: number, component: string): number {
    if (component === 'horse') {
      const slotsPerDrive = this.horseConfig.slotsPerDrive || 2;
      return 2 + index * slotsPerDrive;
    } else {
      return index * 4;
    }
  }

  getAxleSlotCount(index: number, component: string): number {
    if (component === 'trailer') {
      const start = this.getAxleStart(index, component);
      return Math.min(4, this.trailerConfig.slots.length - start);
    }
    return 0;
  }

  initializeConfigurations(): void {
    const config = this.configurations[this.configType];
    this.horseConfig = JSON.parse(JSON.stringify(config.horse));
    this.trailerConfig = JSON.parse(JSON.stringify(config.trailer));

    // Generate slots for horse
    this.horseConfig.slots = this.generateSlots(this.horseConfig, 'horse');

    // Generate slots for trailer
    this.trailerConfig.slots = this.generateSlots(this.trailerConfig, 'trailer');
  }

  generateSlots(config: TyreConfiguration, type: 'horse' | 'trailer'): TyreSlot[] {
    const slots: TyreSlot[] = [];
    let labels: string[] = [];
    if (type === 'trailer') {
      const numAxles = config.numAxles || 2;
      let labelIndex = 1;
      for (let a = 1; a <= numAxles; a++) {
        if (labels.length >= config.slotCount) break;
        labels.push(`T-L${labelIndex}`);
        if (labels.length >= config.slotCount) break;
        labels.push(`T-L${labelIndex + 1}`);
        if (labels.length >= config.slotCount) break;
        labels.push(`T-R${labelIndex}`);
        if (labels.length >= config.slotCount) break;
        labels.push(`T-R${labelIndex + 1}`);
        labelIndex += 2;
      }
    } else {
      // horse
      labels = ['F-L1', 'F-R1'];
      const numDrives = config.numDrives || 0;
      const slotsPerDrive = config.slotsPerDrive || 2;
      const isDual = slotsPerDrive === 4;
      for (let d = 1; d <= numDrives; d++) {
        if (isDual) {
          const outer = 2 * d - 1;
          const inner = 2 * d;
          if (labels.length >= config.slotCount) break;
          labels.push(`R-L${outer}`);
          if (labels.length >= config.slotCount) break;
          labels.push(`R-L${inner}`);
          if (labels.length >= config.slotCount) break;
          labels.push(`R-R${outer}`);
          if (labels.length >= config.slotCount) break;
          labels.push(`R-R${inner}`);
        } else {
          if (labels.length >= config.slotCount) break;
          labels.push(`R-L${d}`);
          if (labels.length >= config.slotCount) break;
          labels.push(`R-R${d}`);
        }
      }
      if (config.hasSpare) {
        if (labels.length < config.slotCount) labels.push('SP-1');
        if (labels.length < config.slotCount) labels.push('SP-2');
      }
    }

    // Create slots from labels
    for (let i = 0; i < Math.min(labels.length, config.slotCount); i++) {
      slots.push({
        id: `${type}-slot-${Math.floor(i / config.cols)}-${i % config.cols}`,
        label: labels[i],
        isActive: false,
        color: type === 'horse' ? 'blue' : 'purple'
      });
    }

    return slots;
  }

  initializeTyreAssignments(): void {

    if (this.isEdit && this.savedComponents && this.savedComponents.length > 0) {
  
      const requiredAssociation =
        this.selectedTypePosition === 'truck' ? 'TRUCK' : 'TRAILER';
  
      const filteredComponents = this.savedComponents.filter(
        (c: any) => c.associationType === requiredAssociation
      );
  
      this.tyreAssignments = filteredComponents.map((t: any) => {
  
        const componentType =
          this.selectedTypePosition === 'truck' ? 'Horse' : 'Trailer';
  
        const match = t.sideSlot?.match(/\(([^)]+)\)/);
  
        return {
          id: t.id,
          tyreNumber: t.serialNumber,
          component: componentType,
          axlePosition: t.axelPosition,
          sideSlot: t.sideSlot,
          selectedSlot: match ? match[1] : undefined
        };
  
      });
  
    }
    else if (this.data && this.data.selectedTyres && this.data.selectedTyres.length > 0) {
  
      this.tyreAssignments = this.data.selectedTyres.map((t: any, i: number) => ({
        id: t.id,
        tyreNumber: t.serialNumber,
        component: 'Horse' as const,
        axlePosition: this.getAxlePositions('Horse')[0],
        sideSlot: this.getSideSlots('Horse', this.getAxlePositions('Horse')[0])[0],
        selectedSlot: undefined
      }));
  
      if (this.selectedTypePosition === 'truck') {
        this.tyreAssignments.forEach((el: any) => {
          this.setTyreComponent(el, 'Horse');
        });
      }
  
      if (this.selectedTypePosition === 'trailer') {
        this.tyreAssignments.forEach((el: any) => {
          this.setTyreComponent(el, 'Trailer');
        });
      }
  
    }
  
    this.totalTyresSelected = this.tyreAssignments.length;
  
    this.updateStatistics();
    this.updateSlotActives();
  }

  updateSlotActives(): void {
    // Deactivate all slots first
    [...this.horseConfig.slots, ...this.trailerConfig.slots].forEach(s => {
      s.isActive = false;
    });
    // Activate slots based on current assignments
    this.tyreAssignments.forEach(tyre => {
      if (tyre.selectedSlot) {
        const allSlots = [...this.horseConfig.slots, ...this.trailerConfig.slots];
        const slot = allSlots.find(s => s.label === tyre.selectedSlot);
        if (slot) {
          slot.isActive = true;
        }
      }
    });
  }

  onSlotClick(slot: TyreSlot, type: 'horse' | 'trailer'): void {
    const component = type === 'horse' ? 'Horse' : 'Trailer';
    // Find the tyre assigned to this slot, if any
    const assignedTyre = this.tyreAssignments.find(t => t.selectedSlot === slot.label);
    if (assignedTyre) {
      // Highlight the assigned tyre
      this.currentHighlightedTyre = assignedTyre.id;
    } else {
      // Optional: Assign to last tyre if unassigned
      const lastTyre = this.tyreAssignments[this.tyreAssignments.length - 1];
      if (lastTyre && !lastTyre.selectedSlot) {
        const newSideSlot = this.getSideSlots(component).find(s => s.includes(`(${slot.label})`));
        if (newSideSlot) {
          lastTyre.sideSlot = newSideSlot;
          // Set appropriate axle based on slot label logic
          if (type === 'horse') {
            if (slot.label.startsWith('F-')) {
              lastTyre.axlePosition = 'Steer Axle';
            } else if (slot.label.startsWith('R-')) {
              const num = parseInt(slot.label.match(/\d+/)![0]);
              lastTyre.axlePosition = `Drive Axle ${Math.ceil(num / (this.horseConfig.slotsPerDrive || 2))}`;
            } else if (slot.label.startsWith('SP-')) {
              lastTyre.axlePosition = 'Spare';
            }
          } else {
            const num = parseInt(slot.label.match(/\d+/)![0]);
            lastTyre.axlePosition = `Axle ${Math.ceil(num / 2)}`;
          }
          if (lastTyre.component !== component) {
            lastTyre.component = component;
          }
          this.onTyreChange(lastTyre);
        }
      }
    }
  }

  addNewTyre(): void {
    const newAssignment = this.createTyreAssignment();
    this.tyreAssignments.push(newAssignment);
    this.totalTyresSelected = this.tyreAssignments.length;
    this.updateStatistics();
    this.updateSlotActives();
  }

  createTyreAssignment(): TyreAssignment {
    const count = this.tyreAssignments.length + 1;
    const defaultComponent = 'Horse'; // Default to Horse
    const defaultAxle = this.getAxlePositions(defaultComponent)[0];
    const defaultSide = this.getSideSlots(defaultComponent, defaultAxle)[0];
    return {
      id: `tyre-${count}`,
      tyreNumber: `SN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
      component: defaultComponent,
      axlePosition: defaultAxle,
      sideSlot: defaultSide,
      selectedSlot: undefined
    };
  }

  removeTyre(index: number): void {
    if (this.tyreAssignments.length > 1) {
      const removedTyre = this.tyreAssignments[index];
      this.tyreAssignments.splice(index, 1);
      // Clear the slot if it was assigned
      if (removedTyre.selectedSlot) {
        const allSlots = [...this.horseConfig.slots, ...this.trailerConfig.slots];
        const slot = allSlots.find(s => s.label === removedTyre.selectedSlot);
        if (slot) {
          slot.isActive = false;
        }
      }
      this.totalTyresSelected = this.tyreAssignments.length;
      this.updateStatistics();
      this.currentHighlightedTyre = null;
      this.updateSlotActives();
    }
  }

  updateStatistics(): void {
    this.assignedToHorse = this.tyreAssignments.filter(t => t.component === 'Horse').length;
    this.assignedToTrailer = this.tyreAssignments.filter(t => t.component === 'Trailer').length;
  }

  saveTyreAssignments(): void {
    console.log('Saving tyre assignments:', this.tyreAssignments);

    // Validate
    if (this.tyreAssignments.length === 0) {
      alert('Please add at least one tyre assignment');
      return;
    }

    // Check for duplicate positions
    const selectedSlots = this.tyreAssignments.map(t => t.selectedSlot).filter(Boolean);
    const duplicates = selectedSlots.length !== new Set(selectedSlots).size;

    if (duplicates) {
      alert('Warning: Duplicate positions detected. These will be flagged.');
    }

    // Return data
    const responseData = {
      success: true,
      responseMessage: 'Tyre assignments saved successfully',
      data: this.tyreAssignments
    };

    this.ref.close(responseData);
  }

  cancel(): void {
    this.ref.close();
  }
}